import styled from "styled-components";
import { Card, Badge } from "@teals/ui-web";

/* ------------------------------------------------------------------ */
/*  Page layout                                                        */
/* ------------------------------------------------------------------ */

export const PageWrapper = styled.div`
  padding: 2rem;
  max-width: 1280px;
  margin: 0 auto;

  @media (max-width: 767px) {
    padding: 1.5rem 1rem;
  }
`;

export const Header = styled.div`
  padding: 2rem 0 1.5rem;

  @media (max-width: 767px) {
    padding: 0 0 1.25rem;
  }
`;

export const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 0.25rem;
`;

export const Subtitle = styled.p`
  color: var(--text-muted);
  font-size: 0.9375rem;
  margin: 0;
`;

export const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  padding-bottom: 1.5rem;
`;

export const SearchSpacer = styled.div`
  flex: 1;
`;

export const FilterBadge = styled(Badge)<{ $active?: boolean }>`
  cursor: pointer;
  opacity: ${(p) => (p.$active ? 1 : 0.5)};
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  @media (max-width: 1023px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: var(--text-muted);
`;

export const ErrorBanner = styled.div`
  padding: 0.75rem 1rem;
  background: var(--danger-bg, #fee2e2);
  color: var(--danger, #dc2626);
  border-radius: 8px;
  margin-bottom: 1rem;
`;

/* ------------------------------------------------------------------ */
/*  Card                                                               */
/* ------------------------------------------------------------------ */

export const DSCard = styled(Card)`
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  border: 1px solid var(--border);
  transition:
    box-shadow 0.2s,
    border-color 0.2s;
  &:hover {
    border-color: var(--accent);
    box-shadow: var(--shadow-md);
  }
`;

export const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
`;

export const CardTitle = styled.div`
  font-size: 1.0625rem;
  font-weight: 600;
  color: var(--text-primary);
`;

export const CardRecords = styled.div`
  font-size: 0.8125rem;
  color: var(--text-muted);
  margin-top: 0.125rem;
`;

export const AgentChipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  flex-wrap: wrap;
`;

export const AgentChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.1875rem 0.5rem;
  border-radius: 6px;
  font-size: 0.6875rem;
  font-weight: 500;
  background: var(--accent-subtle, rgba(99, 102, 241, 0.08));
  color: var(--accent, #6366f1);
  border: 1px solid var(--accent-muted, rgba(99, 102, 241, 0.2));
`;

export const CardFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: auto;
  padding-top: 0.5rem;
  border-top: 1px solid var(--border);
`;

export const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  color: var(--text-muted);
`;

export const DividerDot = styled.span`
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: var(--border-strong, #888);
  flex-shrink: 0;
`;

/* ------------------------------------------------------------------ */
/*  Detail slide-over                                                  */
/* ------------------------------------------------------------------ */

export const DetailSection = styled.div`
  margin-bottom: 1.5rem;
`;

export const DetailSectionTitle = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-muted);
  margin-bottom: 0.75rem;
`;

export const DetailField = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--border);
  &:last-child {
    border-bottom: none;
  }
`;

export const DetailFieldLabel = styled.span`
  font-size: 0.8125rem;
  color: var(--text-muted);
  flex-shrink: 0;
`;

export const DetailFieldValue = styled.span`
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--text-primary);
  min-width: 0;
  text-align: right;
  word-break: break-word;
`;

export const AssociationItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.625rem 0.75rem;
  background: var(--bg-elevated, #f5f5f5);
  border-radius: 8px;
  margin-bottom: 0.5rem;
`;

export const AssociationName = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
`;

export const Divider = styled.hr`
  border: none;
  border-top: 1px solid var(--border);
  margin: 1.5rem 0;
`;

/* ------------------------------------------------------------------ */
/*  Wizard                                                             */
/* ------------------------------------------------------------------ */

export const StepContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const WizardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 1rem 0;
  margin-top: 1.5rem;
  border-top: 1px solid var(--border);
`;

export const FooterActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-left: auto;
`;

export const TypeCard = styled(Card)<{ $selected?: boolean }>`
  cursor: pointer;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem;
  border: 2px solid ${(p) => (p.$selected ? "var(--accent)" : "var(--border)")};
  background: ${(p) => (p.$selected ? "var(--accent-subtle, rgba(99,102,241,0.05))" : "transparent")};
  transition: all 0.2s;
  &:hover {
    border-color: var(--accent-muted, var(--accent));
    background: var(--bg-elevated, #f5f5f5);
  }
`;

export const TypeCardDisabled = styled(TypeCard)`
  opacity: 0.55;
  cursor: not-allowed;
  pointer-events: none;
`;

export const TypeCardIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: var(--bg-elevated, #f5f5f5);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  flex-shrink: 0;
`;

export const TypeCardTitle = styled.div`
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.25rem;
`;

export const TypeCardDesc = styled.div`
  font-size: 0.8125rem;
  color: var(--text-muted);
  line-height: 1.4;
`;

export const ComingSoonTag = styled.span`
  display: inline-block;
  margin-top: 0.375rem;
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
  font-size: 0.6875rem;
  font-weight: 500;
  background: var(--bg-elevated, #f5f5f5);
  color: var(--text-muted);
`;

/* ------------------------------------------------------------------ */
/*  Schema / Upload                                                    */
/* ------------------------------------------------------------------ */

export const UploadZone = styled.div<{ $dragover?: boolean }>`
  border: 2px dashed
    ${(p) => (p.$dragover ? "var(--accent)" : "var(--border-strong, #ccc)")};
  border-radius: 12px;
  padding: 2rem 1.5rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  background: ${(p) => (p.$dragover ? "var(--accent-subtle, rgba(99,102,241,0.05))" : "var(--bg-elevated, #f5f5f5)")};
  &:hover {
    border-color: var(--accent);
    background: var(--accent-subtle, rgba(99, 102, 241, 0.05));
  }
`;

export const UploadZoneIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 0.5rem;
`;

export const UploadZoneText = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary, var(--text-muted));
  margin-bottom: 0.25rem;
`;

export const UploadZoneHint = styled.div`
  font-size: 0.75rem;
  color: var(--text-muted);
`;

export const CodeEditor = styled.textarea`
  width: 100%;
  min-height: 220px;
  padding: 1rem;
  background: var(--code-bg, #1e1e1e);
  border: 1px solid var(--border);
  border-radius: 8px;
  font-family: "SF Mono", "Fira Code", monospace;
  font-size: 0.8125rem;
  line-height: 1.5;
  color: var(--text-primary);
  resize: vertical;
  outline: none;
  &:focus {
    border-color: var(--accent);
    box-shadow: 0 0 0 3px var(--ring, rgba(99, 102, 241, 0.2));
  }
`;

export const CodeBlock = styled.pre`
  padding: 1rem;
  background: var(--code-bg, #1e1e1e);
  border: 1px solid var(--border);
  border-radius: 8px;
  font-family: "SF Mono", "Fira Code", monospace;
  font-size: 0.75rem;
  line-height: 1.6;
  color: var(--text-primary);
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-all;
`;

/* ------------------------------------------------------------------ */
/*  Banners                                                            */
/* ------------------------------------------------------------------ */

export const InfoBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  background: var(--info-subtle, rgba(59, 130, 246, 0.06));
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 8px;
  margin-bottom: 1rem;
  font-size: 0.8125rem;
  color: var(--text-secondary, var(--text-muted));
`;

export const InfoBannerIcon = styled.span`
  font-size: 1rem;
  flex-shrink: 0;
`;

export const SuccessBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: var(--success-subtle, rgba(34, 197, 94, 0.06));
  border: 1px solid rgba(34, 197, 94, 0.25);
  border-radius: 8px;
  margin-bottom: 1.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--success, #22c55e);
`;

/* ------------------------------------------------------------------ */
/*  Copy field                                                         */
/* ------------------------------------------------------------------ */

export const KeyDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: var(--bg-elevated, #f5f5f5);
  border: 1px solid var(--border);
  border-radius: 8px;
  font-family: "SF Mono", "Fira Code", monospace;
  font-size: 0.8125rem;
  color: var(--text-primary);
  word-break: break-all;
`;

export const CopyButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  font-size: 0.875rem;
  color: var(--text-muted);
  flex-shrink: 0;
  &:hover {
    color: var(--accent);
  }
`;

/* ------------------------------------------------------------------ */
/*  Toggle switch                                                      */
/* ------------------------------------------------------------------ */

export const ToggleSwitch = styled.label`
  position: relative;
  width: 36px;
  height: 20px;
  display: inline-block;
  input {
    opacity: 0;
    width: 0;
    height: 0;
  }
`;

export const ToggleSlider = styled.span<{ $checked?: boolean }>`
  position: absolute;
  inset: 0;
  background: ${(p) => (p.$checked ? "var(--accent)" : "var(--border-strong, #ccc)")};
  border-radius: 20px;
  cursor: pointer;
  transition: background 0.2s;
  &::before {
    content: "";
    position: absolute;
    width: 16px;
    height: 16px;
    left: 2px;
    bottom: 2px;
    background: white;
    border-radius: 50%;
    transition: transform 0.2s;
    transform: ${(p) => (p.$checked ? "translateX(16px)" : "translateX(0)")};
  }
`;

/* ------------------------------------------------------------------ */
/*  Loading animation                                                  */
/* ------------------------------------------------------------------ */

export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  text-align: center;
`;

export const LoadingText = styled.div`
  font-size: 0.875rem;
  color: var(--text-muted);
  margin-top: 1rem;
`;

export const ThinkingDots = styled.div`
  display: flex;
  gap: 0.375rem;
  span {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--accent);
    animation: dotPulse 1.4s ease-in-out infinite;
    &:nth-child(2) {
      animation-delay: 0.2s;
    }
    &:nth-child(3) {
      animation-delay: 0.4s;
    }
  }
  @keyframes dotPulse {
    0%,
    80%,
    100% {
      opacity: 0.3;
      transform: scale(0.8);
    }
    40% {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

/* ------------------------------------------------------------------ */
/*  Index detail grid (BYO verify)                                     */
/* ------------------------------------------------------------------ */

export const RemoteDetailGrid = styled.div`
  display: grid;
  gap: 0.5rem;
  margin-top: 1rem;
  padding: 1rem;
  background: var(--bg-elevated, #f5f5f5);
  border-radius: 8px;
  font-size: 0.8125rem;
`;

export const RemoteDetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  span:first-child {
    color: var(--text-muted);
  }
`;

/* ------------------------------------------------------------------ */
/*  Provider cards                                                     */
/* ------------------------------------------------------------------ */

export const ProviderCard = styled(Card)<{
  $selected?: boolean;
  $disabled?: boolean;
}>`
  cursor: ${(p) => (p.$disabled ? "not-allowed" : "pointer")};
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  opacity: ${(p) => (p.$disabled ? 0.55 : 1)};
  pointer-events: ${(p) => (p.$disabled ? "none" : "auto")};
  border: 2px solid ${(p) => (p.$selected ? "var(--accent)" : "var(--border)")};
  background: ${(p) =>
    p.$selected
      ? "var(--accent-subtle, rgba(99,102,241,0.05))"
      : "transparent"};
  transition: all 0.2s;
  &:hover {
    border-color: var(--accent-muted, var(--accent));
    background: var(--bg-elevated, #f5f5f5);
  }
`;

export const ProviderLogo = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: var(--bg-elevated, #f5f5f5);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  flex-shrink: 0;
  color: var(--text-secondary, var(--text-muted));
`;

/* ------------------------------------------------------------------ */
/*  Index selection (BYO)                                              */
/* ------------------------------------------------------------------ */

export const IndexSelectItem = styled.label<{ $selected?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  border: 2px solid ${(p) => (p.$selected ? "var(--accent)" : "var(--border)")};
  background: ${(p) =>
    p.$selected
      ? "var(--accent-subtle, rgba(99,102,241,0.05))"
      : "transparent"};
  border-radius: 10px;
  margin-bottom: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
  &:hover {
    border-color: var(--accent-muted, var(--accent));
  }
  input {
    margin-top: 0.25rem;
  }
`;

export const IndexMeta = styled.div`
  font-size: 0.75rem;
  color: var(--text-muted);
  margin-top: 0.25rem;
`;

/* ------------------------------------------------------------------ */
/*  Tabs (simple inline tab bar, not full @teals/ui-web Tabs)          */
/* ------------------------------------------------------------------ */

export const TabBar = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 1px solid var(--border);
  margin-bottom: 1.25rem;
`;

export const Tab = styled.button<{ $active?: boolean }>`
  background: none;
  border: none;
  border-bottom: 2px solid
    ${(p) => (p.$active ? "var(--accent)" : "transparent")};
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: ${(p) => (p.$active ? 600 : 400)};
  color: ${(p) => (p.$active ? "var(--accent)" : "var(--text-muted)")};
  cursor: pointer;
  transition: all 0.2s;
  &:hover {
    color: var(--accent);
  }
`;

export const FilterStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

export const FilterEditorHint = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  color: var(--text-muted);
  line-height: 1.4;
`;

export const FilterTableLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-secondary);
  margin-top: 0.5rem;
`;

export const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
`;

export const FilterSelect = styled.select`
  min-height: 2.25rem;
  padding: 0.375rem 0.5rem;
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  background: var(--bg-surface);
  color: var(--text-primary);
  font-size: 0.8125rem;
`;
